import { createBrowserHistory } from 'history';
import { useContext, useEffect } from 'react';
import { Navigate, Routes, useNavigate } from 'react-router';
import { Route, unstable_HistoryRouter as ReactRouter } from 'react-router-dom';
import { RouteContext, RouteProvider } from '.';
import withLayout from '../components/common/Layout';
import LoadingPlaceholder from '../components/common/Spinners';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { useNcassAuth0 } from '../hooks/useNcassAuth0';
import { useIsAuthorized } from './hooks/useIsAuthorized';
import { notFoundRoute } from './pages/error/PageNotFound/route';
import { unauthorizedRoute } from './pages/error/PageUnauthorized/route';
import { RouteConfig } from './types/RouteConfig';
import { MembershipImpersonationProvider } from '../components/MembershipImpersonation/MembershipImpersonationContext';
import { maintenanceRoute } from "./pages/maintenance/route";
import { logoutRoute } from './pages/account/logout/route';
import { landingPageRedirectionRoute } from './pages/landing-page-redirection/route';
import settings from "../config";
import { errorMembershipRoute } from "./pages/error/PageMembershipStatusError/route";
import { myRenewRoute } from "./pages/my-account/renew/route";
import { dashboardPageRoute } from "./pages/member-home/route";
import { myPaymentsAndInvoicesRoute } from "./pages/my-account/payment-and-invoices/route";
import { MemberProvider } from '../components/Context/memberContext/MemberContext';
import { MemberPermissionsProvider } from '../components/member-permissions/MemberPermissionsContext';
import { createState as createStatePool, useState as useStatePool } from "state-pool";
import ImpersonatingMemberWidget from '../components/common/ImpersonatingMemberWidget';
import { useAutoReloadOnNewVersion } from '../hooks/useAutoReloadOnNewVersion';
import HasFreeTrialUnitsAlert from '../components/HasFreeTrialUnitsAlert';
import { MembershipInfoProvider } from '../components/Context/MembershipInfoContext';
import { ReferenceTypesProvider } from '../components/Context/ReferenceTypes/ReferenceTypesContext';
import NotificationBanner from '../components/notification-banner/NotificationBanner';


type ActiveRouteProps = {
    route: RouteConfig<'with-page'>,
    refRoute: RouteConfig<'with-page'>,
}

export const isBackendOfflineState = createStatePool<boolean>(false);
export const history = createBrowserHistory();

const Router = () => {
    return (
        <ReactRouter history={history}>
            <RouteProvider>
                <AuthGate>
                    <AppRoutes />
                </AuthGate>                
            </RouteProvider>
        </ReactRouter>
    )
}

const AppRoutes = () => {
    useAutoReloadOnNewVersion();
    useGoogleAnalytics();
    const { flattenedRoutes } = useContext(RouteContext);

    return (
        <Routes>
            {flattenedRoutes.map((flattenedRoute) => {
                const [route, refRoute] = Array.isArray(flattenedRoute)
                    ? flattenedRoute
                    : [flattenedRoute, flattenedRoute]

                return (
                    <Route key={route.path} path={route.path} element={<ActiveRoute route={route} refRoute={refRoute} />} />
                )
            })}
            <Route path='*' element={<ActiveRoute route={notFoundRoute} refRoute={notFoundRoute} />} />
        </Routes>
    );
}

const AuthGate = ({ children }) => {
    const { isLoading, isAuthenticated, loginWithRedirect } = useNcassAuth0();
    const navigate  = useNavigate();


    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            const current = window.location.pathname + window.location.search;

            const targetUrl = current.startsWith(logoutRoute.path)
                ? landingPageRedirectionRoute.path
                : current;

            loginWithRedirect({ appState: { targetUrl } });
        }
    }, [isLoading, isAuthenticated, loginWithRedirect]);

    if (isLoading) {
       return <LoadingPlaceholder />;
    }

    return children;
};

const SystemGuard = ({ user, route, children }) => {
    const [isBackendOffline] = useStatePool(isBackendOfflineState);

    if (route.path === logoutRoute.path) {
        return children;
    }

    if (isBackendOffline && route.path !== maintenanceRoute.path) {
        return <Navigate to={maintenanceRoute.path} replace />;
    }

    if (
        settings.MAINTENANCE_MODE === 'true' &&
        !user.roles.includes("developer") &&
        route.path !== maintenanceRoute.path
    ) {
        return <Navigate to={maintenanceRoute.path} replace />;
    }

    return children;
};

const MembershipGuard = ({ user, route, children }) => {
    const status = user?.ncassAccountStatus;

    const allowedStatuses = ['Active', 'Renew Soon'];

    const allowedRoutes = [
        errorMembershipRoute.path,
        myRenewRoute.path,
        myPaymentsAndInvoicesRoute.path,
        logoutRoute.path,
        dashboardPageRoute.path
    ];

    if (
        status !== undefined &&
        !allowedStatuses.includes(status) &&
        !allowedRoutes.includes(route.path)
    ) {
        return <Navigate to={errorMembershipRoute.path} replace />;
    }

    return children;
};

const AuthorizationGuard = ({ route, children }) => {
    const isAuthorized = useIsAuthorized();

    if (!isAuthorized(route)) {
        return <Navigate to={unauthorizedRoute.path} replace />;
    }

    return children;
};

const RouteContent = ({ route }) => {
    switch (route.context) {
        case 'member':
            return (
                <MembershipImpersonationProvider>
                    <MemberProvider>
                        <ReferenceTypesProvider>
                            <MemberPermissionsProvider>                        
                                <MembershipInfoProvider>
                                    <>
                                        <ImpersonatingMemberWidget/>
                                        <NotificationBanner />
                                        <HasFreeTrialUnitsAlert/>
                                        <route.page />
                                    </>
                                </MembershipInfoProvider>                        
                            </MemberPermissionsProvider>
                        </ReferenceTypesProvider>

                    </MemberProvider>
                </MembershipImpersonationProvider>
            );

        default:
            return <route.page />;
    }
};

const ActiveRoute = withLayout(({ route, refRoute }) => {
    const { setActiveRoute } = useContext(RouteContext);
    const { user } = useNcassAuth0();

    useEffect(() => {
        setActiveRoute(refRoute);
    }, [refRoute, setActiveRoute]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });
    }, [route.path]);

    return (
        <SystemGuard user={user} route={route}>
            <MembershipGuard user={user} route={route}>
                <AuthorizationGuard route={route}>
                    <RouteContent route={route} />
                </AuthorizationGuard>
            </MembershipGuard>
        </SystemGuard>
    );
});


export default Router;
